<template>
  <div>
    <right-panel class="float-right" id="sidebar-edit-organization" width="1000px" :icon="'mdi mdi-plus'" :text="$t('organization.new')" @hidden="rightPanelClosed">
      <div class="pl-5 pr-5">
        <Form :hide-library="true" :closeOnSave="true" />
      </div>
    </right-panel>

    <right-panel class="float-right" id="sidebar-edit-organization-members" width="1000px" :icon="'mdi mdi-plus'" @hidden="rightPanelClosed">
      <div class="pl-5 pr-5" v-if="editedOrganization">
        <ManagementOrganizationMembersView />
      </div>
    </right-panel>

    <right-panel class="float-right" id="sidebar-edit-organization-purchased-feature" width="1000px" @hidden="rightPanelClosed">
      <div class="pl-5 pr-5">
        <h2>{{ $t("purchased-feature.edit") }}</h2>

        <b-card v-if="editedOrganization">
          <h4>{{ editedOrganization.name }}</h4>

          <b-form-checkbox switch size="sm" v-model="purchasedFeatures.hasFirmLimit" :value="true" :unchecked-value="false">
            {{ $t("purchased-feature.firm-limit") }}
          </b-form-checkbox>

          <sweet-input
            v-if="purchasedFeatures.hasFirmLimit"
            v-model.number="purchasedFeatures.firmLimit"
            :isValid="!$v.purchasedFeatures.firmLimit.$error"
            :placeholder="$t('purchased-feature.firm-limit')"
            @input="$v.purchasedFeatures.firmLimit.$touch()"
            type="number"
            layout="vertical"
            inputClass="mb-1"
          />

          <h6 class="mt-4">
            {{ $t("purchased-feature.accessable-products") }}
          </h6>

          <multiselect
            v-model="purchasedFeatures.accessableProducts"
            :placeholder="$t('purchased-feature.accessable-products')"
            :options="['FWSED']"
            :searchable="false"
            :show-labels="false"
            :multiple="true"
          >
          </multiselect>

          <h5 class="mt-4">Erişilebilir Özellikler</h5>
          <div class="d-flex flex-wrap">
            <b-form-checkbox switch size="sm" class="mr-3 mb-2" v-model="purchasedFeatures.questionSet" :value="true" :unchecked-value="false">
              Question Set
            </b-form-checkbox>
            <b-form-checkbox switch size="sm" class="mb-2" v-model="purchasedFeatures.policy" :value="true" :unchecked-value="false">
              Policy
            </b-form-checkbox>
          </div>

          <sweet-button @click="savePurchasedFeatures" type="submit" :disabled="$v.$invalid" variant="success" icon="mdi mdi-check" size="sm" buttonClass="mt-4">
            {{ $t("common.save") }}
          </sweet-button>
        </b-card>
      </div>
    </right-panel>

    <div>
      <h2>{{ $t("organization.list") }}</h2>
    </div>

    <div class="bg-white">
      <table class="table table-bordered table-striped">
        <thead class="bg-secondary text-white">
          <tr>
            <th>{{ $t("common.created-at") }}</th>
            <th>{{ $t("common.name") }}</th>
            <th></th>
            <th>{{ $t("common.owner") }}</th>
            <th>{{ $t("common.workspace") }}</th>

            <th scope="col" style="width: 15%">
              <a href="javascript:;" @click="toggleFilter">
                <span v-if="filterEnabled" class="text-warning">
                  <i class="bx bxs-filter-alt"></i>
                  {{ $t("common.filters-hide") }}
                </span>
                <span v-else class="text-white">
                  <i class="bx bxs-filter-alt"></i>
                  {{ $t("common.filters-show") }}
                </span>
              </a>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-if="filterEnabled">
            <th><date-picker v-model="rangeDate" range :placeholder="$t('common.date')" @change="setDateValue" :lang="currentLanguage"> </date-picker></th>
            <th>
              <input class="form-control " v-model="nameFilter" :placeholder="$t('common.search')" />
            </th>

            <th></th>
            <filter-select
              id="ownerFilter"
              :selected="ownerFilter"
              ref="ownerFilter"
              :placeholder="$t('common.owner')"
              :multiple="false"
              @filter="onAccountChange"
              @changed="onAccountSelect"
              class="mt-2"
            />

            <th>
              <filter-select
                id="workspaceFilter"
                :selected="workspaceFilter"
                ref="workspaceFilter"
                :placeholder="$t('common.workspace')"
                :multiple="false"
                @filter="onWorkspaceChange"
                @changed="onWorkspaceSelect"
              />
            </th>
            <th></th>
          </tr>

          <template v-if="pagination">
            <tr v-for="item in pagination.docs" :key="item._id">
              <td>
                <span v-if="item.createdDate">{{ item.createdDate | date(true) }}</span>
              </td>
              <td>{{ item.name }}</td>
              <td>
                <b v-if="item.features.hasFirmLimit">{{ item.features.firmLimit }} </b>
                <b v-else class="font-size-20 text-muted">&infin;</b>
                <br />
                {{ item.features.accessableProducts.join(", ") }}
              </td>
              <td>{{ item.createdBy.name }}</td>
              <td>{{ item.workspace.name }}</td>

              <td>
                <div class="font-size-16 text-center">
                  <i class="bx bx-user-circle hand text-secondary ml-2" v-b-tooltip.hover :title="$t('organization.members.text')" @click="editMembers(item)"></i>
                  <i class="bx bx-money text-success hand ml-2" v-b-tooltip.hover :title="$t('purchased-feature.edit')" @click="editFeature(item)"></i>
                  <i class="bx bx-pencil text-primary hand ml-2" v-b-tooltip.hover :title="$t('common.edit')" @click="edit(item)"></i>
                  <i class="bx bx-trash-alt text-danger hand ml-2" @click="remove(item)"></i>
                </div>
              </td></tr
          ></template>
        </tbody>
      </table>
    </div>

    <content-loading v-if="loading" />

    <empty v-if="!loading && (!pagination || pagination.docs.length == 0)">
      <h5 class="text-center" id="myText">
        {{ $t("account.system.no-users") }}
      </h5>
    </empty>

    <!-- Begin: Pagination -->
    <div v-if="pagination" class="row">
      <div class="col">
        <ul class="pagination pagination-rounded mb-0">
          <b-pagination v-model="request.options.page" :total-rows="pagination.totalDocs" :per-page="pagination.limit" limit="10" size="sm"></b-pagination>
        </ul>
      </div>
    </div>
    <!-- End: Pagination -->
  </div>
</template>

<script>
import Form from "@modules/organization/management-organization-form";
import ManagementOrganizationMembersView from "@modules/organization/management-organization-members-view";

import DatePicker from "vue2-datepicker";
import { maxValue, minValue, requiredIf } from "vuelidate/lib/validators";

const state = "managementOrganization";
export default {
  components: {
    Form,
    ManagementOrganizationMembersView,

    DatePicker,
  },
  data() {
    return {
      request: {
        options: { page: 1, limit: 10 },
        query: {},
      },
      rangeDate: [],
      dateField: "createdDate",
      nameFilter: undefined,
      ownerFilter: undefined,
      workspaceFilter: undefined,
      filterEnabled: false,
      organization: undefined,
      purchasedFeatures: {
        hasFirmLimit: false,
        firmLimit: 1,
        accessableProducts: [],
        policy: true,
        questionSet: true,
      },
    };
  },
  created() {},
  watch: {
    dateField(n, o) {
      if ((n, n != o)) {
        this.clearDateFilter();
      }
    },
    request: {
      deep: true,
      immediate: true,
      handler() {
        this.debounce(() => this.init());
      },
    },

    nameFilter(n, o) {
      this.request.query = {
        ...this.request.query,
        name: { value: n },
      };
    },
    dateFilter(n, o) {
      if (!n) {
        this.request.query = { ...this.request.query, createdDate: undefined };
        return;
      }
      this.request.query = { ...this.request.query, createdDate: { value: n, isObject: true } };
    },

    ownerFilter(n, o) {
      this.request.query = {
        ...this.request.query,
        owner: { value: n },
      };
    },
    workspaceFilter(n, o) {
      this.request.query = {
        ...this.request.query,
        workspace: { value: n },
      };
    },
  },
  computed: {
    pagination() {
      return this.$store.getters[`${state}/pagination`];
    },
    loading() {
      return this.$store.getters[`${state}/isLoading`];
    },
    editedOrganization() {
      return this.$store.getters[`${state}/editedOrganization`];
    },
  },
  validations: {
    purchasedFeatures: {
      firmLimit: {
        required: requiredIf(function(form) {
          return form.hasFirmLimit;
        }),
        maxValue: (value, vm) => {
          return vm.hasFirmLimit ? maxValue(1000)(value) : true;
        },
        minValue: (value, vm) => {
          return vm.hasFirmLimit ? minValue(1)(value) : true;
        },
      },
      accessableProducts: {},
    },
  },
  methods: {
    clearDateFilter() {
      this.request.query.createdAt = null;
      this.rangeDate = [];
    },
    setDateValue(item) {
      this.rangeDate = item;
      if (item && item.length == 2 && !item.includes(null)) {
        const startTime = new Date(this.rangeDate[0]).setHours(0, 0, 0, 0);
        const endTime = new Date(this.rangeDate[1]).setHours(23, 59, 59, 999);
        this.request = {
          ...this.request,
          query: { ...this.request.query, [this.dateField]: { value: { $gte: startTime, $lte: endTime }, isObject: true } },
        };
      } else {
        this.clearDateFilter();
      }
    },
    init() {
      this.$store.dispatch(`${state}/paginate`, this.request);
    },
    rightPanelClosed() {
      this.$store.commit(`${state}/editedOrganization`, null);
      this.$nextTick(() => {
        if (this.$v && this.$v.$reset) {
          this.$v.$reset();
        }
      });
      this.init();
    },

    edit(item) {
      this.$store.commit(`${state}/editedOrganization`, item);
      this.$root.$emit("bv::toggle::collapse", "sidebar-edit-organization");
    },
    editFeature(item) {
      this.$store.commit(`${state}/editedOrganization`, item);
      this.purchasedFeatures = { ...item.features };
      this.$root.$emit("bv::toggle::collapse", "sidebar-edit-organization-purchased-feature");
    },
    editMembers(item) {
      this.$store.commit(`${state}/editedOrganization`, item);
      this.$root.$emit("bv::toggle::collapse", "sidebar-edit-organization-members");
    },

    async remove(payload) {
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) {
        return;
      }
      try {
        await this.$store.dispatch(`${state}/delete`, { id: payload._id, ownerId: payload?.workspace?.owner._id });
        this.$toast.success(this.$t("common.delete-successful"));
        this.init();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },

    toggleFilter() {
      this.filterEnabled = !this.filterEnabled;
    },

    onAccountSelect(item) {
      if (item) {
        this.request.query = { ...this.request.query, createdBy: { value: item, isObject: true } };
      } else {
        this.request.query["createdBy"] = null;
        delete this.request.query["createdBy"];
      }
    },
    async onAccountChange(value, callback) {
      const response = await this.$store.dispatch(`managementAccount/paginate`, {
        options: { limit: 20 },
        query: { name: { value } },
      });

      callback(response.data?.docs ?? []);
    },
    onWorkspaceSelect(item) {
      if (item) {
        this.request.query = { ...this.request.query, workspace: { value: item, isObject: true } };
      } else {
        this.request.query["workspace"] = null;
        delete this.request.query["workspace"];
      }
    },
    async onWorkspaceChange(value, callback) {
      const response = await this.$store.dispatch(`managementWorkspace/paginate`, {
        options: { limit: 10 },
        query: { name: { value } },
      });

      callback(response.data?.docs ?? []);
    },

    async savePurchasedFeatures() {
      try {
        await this.$store.dispatch(`managementPurchasedOrganizationFeature/update`, {
          payload: this.purchasedFeatures,
          sellable: "ORGANIZATION",
          organizationId: this.editedOrganization._id,
        });
        this.$toast.success(this.$t("common.successful"));
        this.$root.$emit("bv::toggle::collapse", "sidebar-edit-organization-purchased-feature");
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
  },
};
</script>